import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../../../lib/api/client';
import { useBoardStore } from '../../../stores/boardStore';

/**
 * usePagePayload - Hook to fetch specialized analytics for a specific Board Page.
 * Migrates data processing from clientside to backend.
 *
 * The backend always returns a fresh heuristic verdict computed from
 * current financial data. LLM verdicts are session-only (set via
 * useDeepVerdict's cache update) and reset on the next refetch.
 *
 * Invalidated by useBoardFullData whenever board-payload syncs,
 * ensuring verdicts reset on any data recalculation (entity changes,
 * channel additions, spec changes, etc.).
 */
export const usePagePayload = (boardId: string, pageId: string) => {
  const { dateRange } = useBoardStore();

  return useQuery({
    queryKey: ['page-payload', boardId, pageId, dateRange.startDate, dateRange.endDate],
    queryFn: async () => {
      const response = await apiClient.get(`/boards/${boardId}/pages/${pageId}/payload`, {
        params: {
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        }
      });
      const payload = response.data;
      if (!payload.success) {
        throw new Error(payload.error || 'Failed to fetch page payload');
      }
      return payload.data || null;
    },
    enabled: !!boardId && !!pageId && !!dateRange.startDate && !!dateRange.endDate,
    staleTime: 5 * 60 * 1000, // 5 minutes cache
  });
};
